import { FC, useState, useRef, useEffect } from "react";
import Input, { InputProps } from "@cloudscape-design/components/input";
import Spinner from "@cloudscape-design/components/spinner";
import Icon from "@cloudscape-design/components/icon";
import { CancelableEventHandler } from "@cloudscape-design/components/internal/events";

interface InlineEditFieldProps {
    label: string;
    value: string;
    onSave: (newValue: string) => Promise<void>;
    disabled?: boolean;
}

const InlineEditField: FC<InlineEditFieldProps> = ({ label, value, onSave, disabled }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);
    const [isSaving, setIsSaving] = useState(false);
    const inputRef = useRef<InputProps.Ref>(null);

    useEffect(() => {
        setEditValue(value);
    }, [value]);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isEditing]);

    const enterEditMode = () => {
        if (disabled || isSaving) return;
        setEditValue(value);
        setIsEditing(true);
    };

    const cancelEdit = () => {
        setEditValue(value);
        setIsEditing(false);
    };

    const saveEdit = async () => {
        if (editValue === value) {
            setIsEditing(false);
            return;
        }

        setIsSaving(true);
        try {
            await onSave(editValue);
            setIsEditing(false);
        } catch {
            setEditValue(value);
            setIsEditing(false);
        } finally {
            setIsSaving(false);
        }
    };

    const handleKeyDown: CancelableEventHandler<InputProps.KeyDetail> = (event) => {
        const { key } = event.detail;
        if (key === "Escape") {
            cancelEdit();
        } else if (key === "Enter") {
            saveEdit();
        }
    };

    if (isEditing) {
        return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <label style={{ fontWeight: "bold", minWidth: "120px" }}>{label}</label>
                <Input
                    value={editValue}
                    onChange={({ detail }) => setEditValue(detail.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={saveEdit}
                    disabled={isSaving}
                    ref={inputRef}
                    ariaLabel={label}
                />
                {isSaving && <Spinner size="normal" />}
            </div>
        );
    }

    return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <label style={{ fontWeight: "bold", minWidth: "120px" }}>{label}</label>
            <span>{value}</span>
            {!disabled && (
                <button
                    onClick={enterEditMode}
                    style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "4px",
                        display: "inline-flex",
                        alignItems: "center",
                    }}
                    aria-label={`Edit ${label}`}
                >
                    <Icon name="edit" />
                </button>
            )}
        </div>
    );
};

export default InlineEditField;
